/**
 * Accessibility utilities for improving application accessibility
 */

import { safeInsertBefore, safeRemoveChild, safeAppendChild, waitForDOMReady, isNodeInDocument } from './dom-helpers';
import { logError, logWarning } from './error-logger';

// Focus trap for modals and dialogs
export const createFocusTrap = (containerElement: HTMLElement) => {
  const focusableElements = containerElement.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  const firstElement = focusableElements[0] as HTMLElement;
  const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
  
  const handleTabKey = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    
    if (e.shiftKey) {
      // If shift + tab and on first element, move to last
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // If tab and on last element, move to first
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };
  
  // Focus the first element
  firstElement?.focus();
  
  // Add event listener
  document.addEventListener('keydown', handleTabKey);
  
  // Return cleanup function
  return () => {
    document.removeEventListener('keydown', handleTabKey);
  };
};

// Escape key handler
export const createEscapeHandler = (callback: () => void) => {
  const handleEscapeKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      callback();
    }
  };
  
  document.addEventListener('keydown', handleEscapeKey);
  
  return () => {
    document.removeEventListener('keydown', handleEscapeKey);
  };
};

// Skip to content link
export const createSkipToContentLink = (contentId: string) => {
  let link: HTMLAnchorElement | null = null;
  let mounted = false;

  const insertLink = async () => {
    try {
      await waitForDOMReady();

      if (!document.body) {
        logWarning('createSkipToContentLink: document.body not available');
        return;
      }

      link = document.createElement('a');
      link.href = `#${contentId}`;
      link.className = 'sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:p-4 focus:bg-white focus:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500';
      link.textContent = 'Passer au contenu principal';

      const result = safeInsertBefore(document.body, link, document.body.firstChild);

      if (!result.success) {
        logError('createSkipToContentLink: failed to insert link', {
          location: 'createSkipToContentLink',
          contentId,
          hasBody: !!document.body,
          hasFirstChild: !!document.body.firstChild,
          bodyChildren: document.body.childNodes.length
        }, result.error);
        return;
      }

      mounted = true;
    } catch (error) {
      logError('createSkipToContentLink: unexpected error', {
        location: 'createSkipToContentLink',
        contentId
      }, error as Error);
    }
  };

  insertLink();

  return () => {
    if (!link || !mounted) return;

    try {
      if (!isNodeInDocument(link)) {
        mounted = false;
        return;
      }

      const result = safeRemoveChild(document.body, link);

      if (!result.success) {
        logError('createSkipToContentLink: cleanup failed', {
          location: 'createSkipToContentLink:cleanup',
          linkInDocument: isNodeInDocument(link),
          hasParent: !!link.parentNode
        }, result.error);
      }

      mounted = false;
      link = null;
    } catch (error) {
      logError('createSkipToContentLink: cleanup error', {
        location: 'createSkipToContentLink:cleanup'
      }, error as Error);
    }
  };
};

// Announce messages to screen readers
export const announceToScreenReader = (message: string, politeness: 'polite' | 'assertive' = 'polite') => {
  let announcer: HTMLDivElement | null = null;
  let timeoutId1: number | null = null;
  let timeoutId2: number | null = null;
  let mounted = false;

  try {
    if (!document.body) {
      logWarning('announceToScreenReader: document.body not available');
      return;
    }

    announcer = document.createElement('div');
    announcer.setAttribute('aria-live', politeness);
    announcer.setAttribute('aria-atomic', 'true');
    announcer.className = 'sr-only';

    const appendResult = safeAppendChild(document.body, announcer);

    if (!appendResult.success) {
      logError('announceToScreenReader: failed to append announcer', {
        location: 'announceToScreenReader',
        message,
        politeness
      }, appendResult.error);
      return;
    }

    mounted = true;

    timeoutId1 = window.setTimeout(() => {
      if (!announcer || !mounted || !isNodeInDocument(announcer)) {
        return;
      }

      announcer.textContent = message;

      timeoutId2 = window.setTimeout(() => {
        if (!announcer || !mounted || !isNodeInDocument(announcer)) {
          return;
        }

        const removeResult = safeRemoveChild(document.body, announcer);

        if (!removeResult.success) {
          logError('announceToScreenReader: cleanup failed', {
            location: 'announceToScreenReader:cleanup',
            message,
            announcerInDocument: isNodeInDocument(announcer),
            hasParent: !!announcer.parentNode
          }, removeResult.error);
        }

        mounted = false;
        announcer = null;
      }, 3000);
    }, 100);
  } catch (error) {
    logError('announceToScreenReader: unexpected error', {
      location: 'announceToScreenReader',
      message,
      politeness
    }, error as Error);

    if (timeoutId1) window.clearTimeout(timeoutId1);
    if (timeoutId2) window.clearTimeout(timeoutId2);
  }
};

// Check contrast ratio
export const checkContrastRatio = (foreground: string, background: string): number => {
  // Convert hex to rgb
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };
  
  // Calculate relative luminance
  const calculateLuminance = (r: number, g: number, b: number) => {
    const a = [r, g, b].map(v => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
  };
  
  const rgb1 = hexToRgb(foreground);
  const rgb2 = hexToRgb(background);
  
  if (!rgb1 || !rgb2) return 0;
  
  const l1 = calculateLuminance(rgb1.r, rgb1.g, rgb1.b);
  const l2 = calculateLuminance(rgb2.r, rgb2.g, rgb2.b);
  
  const ratio = (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
  
  return parseFloat(ratio.toFixed(2));
};

// Add keyboard navigation to custom components
export const makeKeyboardNavigable = (
  containerElement: HTMLElement,
  itemSelector: string,
  onSelect: (element: HTMLElement) => void
) => {
  let currentIndex = -1;
  const items = Array.from(containerElement.querySelectorAll(itemSelector)) as HTMLElement[];
  
  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        e.preventDefault();
        currentIndex = (currentIndex + 1) % items.length;
        items[currentIndex].focus();
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        e.preventDefault();
        currentIndex = (currentIndex - 1 + items.length) % items.length;
        items[currentIndex].focus();
        break;
      case 'Enter':
      case ' ': // Space
        e.preventDefault();
        if (currentIndex >= 0) {
          onSelect(items[currentIndex]);
        }
        break;
      case 'Home':
        e.preventDefault();
        currentIndex = 0;
        items[currentIndex].focus();
        break;
      case 'End':
        e.preventDefault();
        currentIndex = items.length - 1;
        items[currentIndex].focus();
        break;
    }
  };
  
  containerElement.addEventListener('keydown', handleKeyDown);
  
  // Set tabindex and role
  items.forEach(item => {
    item.setAttribute('tabindex', '0');
    item.setAttribute('role', 'option');
  });
  
  containerElement.setAttribute('role', 'listbox');
  containerElement.setAttribute('aria-orientation', 'vertical');
  
  return () => {
    containerElement.removeEventListener('keydown', handleKeyDown);
  };
};

// Check for common accessibility issues
export const checkAccessibility = (element: HTMLElement) => {
  const issues: string[] = [];
  
  // Check for images without alt text
  const images = element.querySelectorAll('img:not([alt])');
  if (images.length > 0) {
    issues.push(`Found ${images.length} images without alt text`);
  }
  
  // Check for form elements without labels
  const formElements = element.querySelectorAll('input, select, textarea');
  formElements.forEach(el => {
    const input = el as HTMLInputElement;
    const id = input.id;
    if (!id) {
      issues.push(`Form element without ID: ${input.outerHTML.slice(0, 100)}`);
      return;
    }
    
    const label = element.querySelector(`label[for="${id}"]`);
    if (!label && !input.hasAttribute('aria-label') && !input.hasAttribute('aria-labelledby')) {
      issues.push(`Form element without label: ${id}`);
    }
  });
  
  // Check for buttons without accessible names
  const buttons = element.querySelectorAll('button');
  buttons.forEach(button => {
    if (!button.textContent?.trim() && !button.hasAttribute('aria-label') && !button.hasAttribute('aria-labelledby')) {
      issues.push(`Button without accessible name: ${button.outerHTML.slice(0, 100)}`);
    }
  });
  
  // Check for insufficient color contrast (would need color values)
  
  // Check for missing landmark roles
  const hasMain = element.querySelector('main, [role="main"]');
  if (!hasMain) {
    issues.push('No main landmark found');
  }
  
  const hasNav = element.querySelector('nav, [role="navigation"]');
  if (!hasNav) {
    issues.push('No navigation landmark found');
  }
  
  return issues;
};

export default {
  createFocusTrap,
  createEscapeHandler,
  createSkipToContentLink,
  announceToScreenReader,
  checkContrastRatio,
  makeKeyboardNavigable,
  checkAccessibility
};